import { useState, useEffect } from 'react';
import axios from '../common/api/request';
import { 
  DesignatedOperatorRelation, 
} from '../types/designatedOperatorRelations';

export interface UseDesignatedOperatorRelationsReturn {
  relations: DesignatedOperatorRelation[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  saveRelations: (relations: DesignatedOperatorRelation[]) => Promise<void>;
}

export function useDesignatedOperatorRelations(year: number, enabled: boolean = true): UseDesignatedOperatorRelationsReturn {
  const [relations, setRelations] = useState<DesignatedOperatorRelation[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRelations = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await axios.get<DesignatedOperatorRelation[]>(
        `/designated-operator-relations/year?year=${year}`
      );
      setRelations(response.data);
    } catch (err) {
      console.error('Failed to fetch designated operator relations:', err);
      setError('Failed to load designated operator relations');
      setRelations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const saveRelations = async (relationsToSave: DesignatedOperatorRelation[]) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Send the array directly, not wrapped in an object
      await axios.post('/designated-operator-relations/bulk', relationsToSave);
      
      // Refresh the data after successful save
      await fetchRelations();
    } catch (err) {
      console.error('Failed to save designated operator relations:', err);
      setError('Failed to save designated operator relations');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch relations if enabled and we have a valid year (not NaN, not 0, and greater than 0)
    if (enabled && year && !isNaN(year) && year > 0) {
      fetchRelations();
    } else {
      // Reset state when disabled or year is invalid
      setRelations([]);
      setIsLoading(false);
      setError(null);
    }
  }, [year, enabled]);

  return {
    relations,
    isLoading,
    error,
    refetch: fetchRelations,
    saveRelations
  };
}
